/**
 * iso/depth.ts — Isometric Depth Sorting
 *
 * The illusion of isometric 3D rests entirely on drawing things in the right
 * order. A chair in front of a table must be drawn after it. A character
 * walking behind a bookshelf must vanish behind it. Get this wrong and the
 * whole world looks broken. Get it right and it feels like magic.
 *
 * The formula below is a weighted painter's algorithm: sort by the "back
 * corner" of each entity's footprint, with Z as a minor key so elevated
 * objects (flying birds, floating lamps) render correctly against walls.
 */

// ─── Interface ───────────────────────────────────────────────────────────────

/**
 * Anything that can be depth-sorted. The iso/ module knows nothing about
 * characters or furniture specifically — it only knows about things that
 * occupy space in the isometric world.
 *
 * `height` is the entity's vertical extent in world units (used as a
 * tiebreaker for objects that share a base position — a tall bookcase
 * should appear behind a low coffee table at the same grid cell).
 */
export interface Depthable {
  /** World X coordinate (increases east) */
  wx: number;
  /** World Y coordinate (increases south) */
  wy: number;
  /** World Z coordinate / elevation (increases upward) */
  wz: number;
  /** Entity height in world units — used as tiebreaker */
  height: number;
}

// ─── Depth Computation ───────────────────────────────────────────────────────

/**
 * Compute a single numeric sort key for an isometric entity.
 *
 * The painter's algorithm for isometric: things with larger (wx + wy) are
 * "deeper" in the scene — further back in the southeast direction — and must
 * be drawn first so nearer things paint over them.
 *
 * Formula: `depth = (wy + wx) * 1000 + wz * 10 + tiebreaker`
 *
 * The `* 1000` gives room for wz sub-sorting within a given XY "row."
 * The `* 10` gives room for the height tiebreaker within the same XY+Z.
 *
 * @param wx       World X
 * @param wy       World Y
 * @param wz       World Z (elevation)
 * @param entityH  Entity height (tiebreaker — shorter things in front)
 * @returns        Depth sort key. Lower = drawn first (further back).
 */
export function computeDepth(
  wx: number,
  wy: number,
  wz: number,
  entityH: number
): number {
  // Taller objects at the same position should be drawn earlier (behind).
  // We invert height so that height=2 produces a *smaller* tiebreaker than
  // height=0.5, meaning the taller object sorts behind the shorter one.
  // We clamp to 9 so it fits in the single digit reserved above wz.
  const tiebreaker = Math.max(0, 9 - Math.floor(entityH * 4));

  return (wy + wx) * 1000 + wz * 10 + tiebreaker;
}

// ─── Sorting ─────────────────────────────────────────────────────────────────

/**
 * Sort an array of Depthable entities in place for correct isometric rendering.
 *
 * **Draw order:** index 0 should be drawn first (furthest back).
 *
 * Stable: entities at identical depth values maintain their original insertion
 * order. This matters for furniture placed on the same tile — the order you
 * placed them in defines which appears "in front" when depths collide.
 *
 * Performance note (CLAUDE.md law #8): only call this when entities actually
 * move or are added/removed. Cache the sorted order between frames.
 *
 * @param entities  Array to sort in place. Modified directly.
 */
export function depthSort<T extends Depthable>(entities: T[]): void {
  // Pre-compute depth keys to avoid recomputing inside the comparator.
  // The comparator runs O(n log n) times; we want each call to be O(1).
  const keys = new Float64Array(entities.length);
  for (let i = 0; i < entities.length; i++) {
    const e = entities[i];
    keys[i] = computeDepth(e.wx, e.wy, e.wz, e.height);
  }

  // Stable sort by index-pairing so that equal depths preserve original order.
  // We sort an index array, then permute entities in place.
  const indices = Array.from({ length: entities.length }, (_, i) => i);

  indices.sort((a, b) => {
    const diff = keys[a] - keys[b];
    if (diff !== 0) return diff;
    // Same depth — preserve insertion order (stable)
    return a - b;
  });

  // Apply the permutation in place using a temporary copy.
  // For typical room sizes (< 200 entities) this allocation is negligible.
  const sorted = indices.map((i) => entities[i]);
  for (let i = 0; i < entities.length; i++) {
    entities[i] = sorted[i];
  }
}

// ─── Utilities ───────────────────────────────────────────────────────────────

/**
 * Check whether two Depthables are at the same depth level.
 * Useful for deciding whether to re-sort after a small movement.
 */
export function sameDepth(a: Depthable, b: Depthable): boolean {
  return (
    Math.abs(computeDepth(a.wx, a.wy, a.wz, a.height) -
             computeDepth(b.wx, b.wy, b.wz, b.height)) < 0.5
  );
}

/**
 * Returns a new sorted array without modifying the original.
 * Use when you need the sorted order but can't mutate (e.g., React-style
 * patterns or when the original array is shared).
 */
export function depthSorted<T extends Depthable>(entities: T[]): T[] {
  const copy = [...entities];
  depthSort(copy);
  return copy;
}
